import { boats, builderNotes, letterCopy, mapBoats } from "../content";
import { isBoatStyleId, type BoatStyleId } from "./boatFleet";

/* ------------------------------------------------------------------ *
 * Same Boat: The Harbour, a small top-down sailing game.
 * This module is framework-free: state, physics, and interaction only.
 * React drives it (input + RAF) and renders the canvas via render.ts.
 * ------------------------------------------------------------------ */

export const WORLD = { w: 2600, h: 2000 };

export type Vec = { x: number; y: number };

export type Port = { id: string; x: number; y: number; label: string; line: string; reply: string };
export type Bottle = { id: string; x: number; y: number; text: string };
export type Raft = { id: string; x: number; y: number };
export type Island = { x: number; y: number; r: number };
export type FleetBoat = { id: string; name: string; blurb: string; x: number; y: number; dir: 1 | -1; speed: number; heading: number };
export type LandmarkKind = "table" | "workshop" | "noticewall";
export type Landmark = { id: string; kind: LandmarkKind; x: number; y: number; name: string };
export type RuleBuoy = { id: string; index: number; x: number; y: number };
export type Whale = { x: number; y: number; dx: number; dy: number; t: number; surfaced: boolean };

export type NearbyKind = "port" | "crew" | "bottle" | "lighthouse" | "harbour" | "fleet" | "landmark" | "rule" | "whale";
export type Nearby = {
  kind: NearbyKind;
  id: string;
  x: number;
  y: number;
  prompt: string;
} | null;

export type Input = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  pointerActive: boolean;
  pointerScreen: Vec;
};

export type Snapshot = {
  vessel: string | null;
  crew: string[];
  visited: number;
  ports: number;
  notesRead: number;
  logRead: boolean;
  rules: number;
  rulesTotal: number;
  landmarks: number;
  landmarksTotal: number;
  whaleFound: boolean;
};

export const MAX_CREW = 4;

/* Eight ports drawn from the "what boat are you in" set. */
const PORT_IDS = ["customers", "fundraising", "sales", "product", "ai", "hiring", "launching", "next"];
const PORT_POS: Record<string, Vec> = {
  customers: { x: 1300, y: 1180 },
  fundraising: { x: 620, y: 1360 },
  sales: { x: 1980, y: 1360 },
  product: { x: 780, y: 720 },
  ai: { x: 1880, y: 760 },
  hiring: { x: 420, y: 1040 },
  launching: { x: 2200, y: 1040 },
  next: { x: 1300, y: 540 },
};

function buildPorts(): Port[] {
  return PORT_IDS.map((id) => {
    const b = boats.find((x) => x.id === id)!;
    const p = PORT_POS[id];
    return { id, x: p.x, y: p.y, label: b.label, line: b.line, reply: b.reply };
  });
}

const BOTTLE_POS: Vec[] = [
  { x: 1010, y: 1500 },
  { x: 1740, y: 1540 },
  { x: 1060, y: 980 },
  { x: 1640, y: 1150 },
];

const RAFT_POS: Vec[] = [
  { x: 720, y: 1150 },
  { x: 1900, y: 1150 },
  { x: 1300, y: 1450 },
];

const ISLANDS: Island[] = [
  { x: 1300, y: 900, r: 150 },
  { x: 600, y: 860, r: 110 },
  { x: 2010, y: 980, r: 120 },
  { x: 1560, y: 1440, r: 96 },
  { x: 980, y: 1250, r: 84 },
];

const FLEET_LANES: { id: string; y: number; dir: 1 | -1; speed: number; x0: number }[] = [
  { id: "first10", y: 1700, dir: 1, speed: 58, x0: 300 },
  { id: "pipeline", y: 1636, dir: -1, speed: 46, x0: 1700 },
  { id: "raise", y: 404, dir: 1, speed: 52, x0: 1000 },
  { id: "build", y: 332, dir: -1, speed: 66, x0: 2200 },
];

const LANDMARKS: Landmark[] = [
  { id: "lm-table", kind: "table", x: 500, y: 1560, name: "Table Island" },
  { id: "lm-workshop", kind: "workshop", x: 780, y: 300, name: "The Workshop" },
  { id: "lm-noticewall", kind: "noticewall", x: 2160, y: 1600, name: "The Notice Wall" },
];

const RULE_POS: Vec[] = [
  { x: 1080, y: 1620 },
  { x: 1720, y: 700 },
  { x: 450, y: 1250 },
  { x: 2250, y: 1250 },
  { x: 1150, y: 600 },
];

export type World = {
  w: number;
  h: number;
  dock: Vec;
  harbour: Vec;
  lighthouse: Vec & { paragraphs: string[]; signoff: string; signoffNote: string };
  ports: Port[];
  bottles: Bottle[];
  rafts: Raft[];
  islands: Island[];
  fleet: FleetBoat[];
  landmarks: Landmark[];
  rules: RuleBuoy[];
  whale: Whale;
};

function buildWorld(): World {
  return {
    w: WORLD.w,
    h: WORLD.h,
    dock: { x: 1300, y: 1720 },
    harbour: { x: 1300, y: 230 },
    lighthouse: {
      x: 300,
      y: 560,
      paragraphs: letterCopy.paragraphs,
      signoff: letterCopy.signoff,
      signoffNote: letterCopy.signoffNote,
    },
    ports: buildPorts(),
    bottles: BOTTLE_POS.slice(0, Math.min(4, builderNotes.length)).map((p, i) => ({
      id: `bottle-${i}`,
      x: p.x,
      y: p.y,
      text: builderNotes[i],
    })),
    rafts: RAFT_POS.map((p, i) => ({ id: `raft-${i}`, x: p.x, y: p.y })),
    islands: ISLANDS,
    fleet: FLEET_LANES.map((l) => {
      const m = mapBoats.find((b) => b.id === l.id)!;
      return { id: `fleet-${l.id}`, name: m.name, blurb: m.blurb, x: l.x0, y: l.y, dir: l.dir, speed: l.speed, heading: l.dir === 1 ? 0 : Math.PI };
    }),
    landmarks: LANDMARKS,
    rules: RULE_POS.map((p, i) => ({ id: `rule-${i}`, index: i, x: p.x, y: p.y })),
    whale: { x: 1150, y: 1770, dx: 20, dy: 7, t: 0, surfaced: false },
  };
}

const BOAT_R = 26;
const ACCEL = 360;
const MAX_SPEED = 260;
const TURN_RATE = 2.8;
const R = {
  port: 96,
  crew: 78,
  bottle: 64,
  lighthouse: 150,
  harbour: 130,
  fleet: 78,
  landmark: 118,
  rule: 62,
  whale: 100,
};

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function angleTowards(cur: number, target: number, maxStep: number) {
  let diff = target - cur;
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return cur + clamp(diff, -maxStep, maxStep);
}

export type Trail = { x: number; y: number };

export class HarbourEngine {
  world = buildWorld();
  boat = { x: this.world.dock.x, y: this.world.dock.y - 100, heading: -Math.PI / 2, heel: 0 };
  vel: Vec = { x: 0, y: 0 };
  camera: Vec = { x: this.boat.x, y: this.boat.y };
  view = { w: 800, h: 600 };
  time = 0;
  reducedMotion = false;
  trail: Trail[] = [];
  nearby: Nearby = null;

  visited = new Set<string>();
  readBottles = new Set<string>();
  rescuedRafts = new Set<string>();
  collectedRules = new Set<string>();
  seenLandmarks = new Set<string>();
  crew: string[] = [];
  claimed: string | null = null;
  boatStyle: BoatStyleId | null = null;
  logRead = false;
  whaleFound = false;

  get speed() {
    return Math.hypot(this.vel.x, this.vel.y);
  }

  /** Passing boats and the whale keep moving whether or not you're sailing. */
  private advanceAmbient(dt: number) {
    for (const f of this.world.fleet) {
      f.x += f.dir * f.speed * dt;
      if (f.dir === 1 && f.x > this.world.w + 140) f.x = -140;
      if (f.dir === -1 && f.x < -140) f.x = this.world.w + 140;
    }
    const wl = this.world.whale;
    wl.t += dt;
    wl.x += wl.dx * dt;
    wl.y += wl.dy * dt;
    if (wl.x < 220 || wl.x > this.world.w - 220) wl.dx *= -1;
    if (wl.y < 1360 || wl.y > this.world.h - 120) wl.dy *= -1;
    wl.surfaced = Math.sin(wl.t * 0.5) > 0.35;
  }

  update(dt: number, input: Input) {
    this.time += dt;
    this.advanceAmbient(dt);
    const b = this.boat;

    // steering: keyboard turn, else steer toward held pointer
    let turnInput = 0;
    if (input.left) turnInput -= 1;
    if (input.right) turnInput += 1;

    let thrusting = input.up;
    if (turnInput !== 0) {
      b.heading += turnInput * TURN_RATE * dt;
    } else if (input.pointerActive) {
      const tx = this.camera.x + (input.pointerScreen.x - this.view.w / 2);
      const ty = this.camera.y + (input.pointerScreen.y - this.view.h / 2);
      const target = Math.atan2(ty - b.y, tx - b.x);
      const far = Math.hypot(tx - b.x, ty - b.y);
      b.heading = angleTowards(b.heading, target, TURN_RATE * dt);
      if (far > 26) thrusting = true;
    }

    if (thrusting) {
      this.vel.x += Math.cos(b.heading) * ACCEL * dt;
      this.vel.y += Math.sin(b.heading) * ACCEL * dt;
    }

    // drag (stronger while braking)
    const drag = input.down ? 4.2 : 1.7;
    const damp = 1 / (1 + drag * dt);
    this.vel.x *= damp;
    this.vel.y *= damp;

    const sp = this.speed;
    if (sp > MAX_SPEED) {
      this.vel.x = (this.vel.x / sp) * MAX_SPEED;
      this.vel.y = (this.vel.y / sp) * MAX_SPEED;
    }

    // Split fast movement into short sweeps. This prevents the boat crossing a
    // narrow buoy, dock edge, or moving vessel between rendered frames.
    const distance = this.speed * dt;
    const steps = Math.max(1, Math.ceil(distance / 7));
    for (let step = 0; step < steps; step += 1) {
      b.x += (this.vel.x * dt) / steps;
      b.y += (this.vel.y * dt) / steps;
      this.resolveWorldCollisions();
      this.clampBoatToWorld();
    }

    // world bounds
    this.clampBoatToWorld();

    // heel: lean into the turn
    const targetHeel = clamp(turnInput * 0.32 + (input.pointerActive ? 0 : 0), -0.4, 0.4);
    b.heel += (targetHeel - b.heel) * Math.min(1, dt * 6);

    // Store trail points by distance, not by frame. Repeating the same point
    // while stationary made crew followers creep into and hide beneath the
    // player boat after a few seconds.
    const lastTrailPoint = this.trail[this.trail.length - 1];
    if (!lastTrailPoint || Math.hypot(b.x - lastTrailPoint.x, b.y - lastTrailPoint.y) >= 3) {
      this.trail.push({ x: b.x, y: b.y });
      if (this.trail.length > 240) this.trail.shift();
    }

    // camera follow, clamped to world with a little overscroll
    const follow = Math.min(1, dt * 3.4);
    this.camera.x += (b.x - this.camera.x) * follow;
    this.camera.y += (b.y - this.camera.y) * follow;
    this.clampCamera();

    this.nearby = this.findNearby();
  }

  /** Push the circular boat hull outside a solid circular object. */
  private resolveCircle(cx: number, cy: number, radius: number) {
    const dx = this.boat.x - cx;
    const dy = this.boat.y - cy;
    const minimum = radius + BOAT_R;
    const distance = Math.hypot(dx, dy);
    if (distance >= minimum) return;

    // When centres coincide, use the reverse velocity as a stable escape
    // direction; fall back south so corrupt/old saves can still recover.
    const speed = this.speed;
    const nx = distance > 0.001 ? dx / distance : speed > 0.001 ? -this.vel.x / speed : 0;
    const ny = distance > 0.001 ? dy / distance : speed > 0.001 ? -this.vel.y / speed : 1;
    this.boat.x = cx + nx * minimum;
    this.boat.y = cy + ny * minimum;
    this.removeInwardVelocity(nx, ny);
  }

  /** Push the circular boat hull outside an axis-aligned solid object. */
  private resolveBox(cx: number, cy: number, halfWidth: number, halfHeight: number) {
    const left = cx - halfWidth - BOAT_R;
    const right = cx + halfWidth + BOAT_R;
    const top = cy - halfHeight - BOAT_R;
    const bottom = cy + halfHeight + BOAT_R;
    const b = this.boat;
    if (b.x <= left || b.x >= right || b.y <= top || b.y >= bottom) return;

    const exits = [
      { distance: b.x - left, nx: -1, ny: 0, x: left, y: b.y },
      { distance: right - b.x, nx: 1, ny: 0, x: right, y: b.y },
      { distance: b.y - top, nx: 0, ny: -1, x: b.x, y: top },
      { distance: bottom - b.y, nx: 0, ny: 1, x: b.x, y: bottom },
    ];
    const exit = exits.reduce((best, candidate) => (candidate.distance < best.distance ? candidate : best));
    b.x = exit.x;
    b.y = exit.y;
    this.removeInwardVelocity(exit.nx, exit.ny);
  }

  private removeInwardVelocity(nx: number, ny: number) {
    const inward = this.vel.x * nx + this.vel.y * ny;
    if (inward < 0) {
      this.vel.x -= inward * nx;
      this.vel.y -= inward * ny;
    }
  }

  private resolveWorldCollisions() {
    // Two passes settle corners where compound shapes meet without jitter.
    for (let pass = 0; pass < 2; pass += 1) {
      for (const island of this.world.islands) this.resolveCircle(island.x, island.y, island.r);

      // The lighthouse art is a narrow tower on a wider stone island.
      const lighthouse = this.world.lighthouse;
      this.resolveCircle(lighthouse.x, lighthouse.y + 62, 72);
      this.resolveBox(lighthouse.x, lighthouse.y - 14, 35, 78);

      // Boatyard dock. The player now launches safely below this boundary.
      this.resolveBox(this.world.dock.x, this.world.dock.y + 20, 72, 45);

      // Same Boat Harbour is a curved quay with a central landing pier. These
      // compound pieces leave navigable water around it while blocking stone.
      const harbour = this.world.harbour;
      this.resolveCircle(harbour.x - 145, harbour.y + 5, 64);
      this.resolveCircle(harbour.x + 145, harbour.y + 5, 64);
      this.resolveBox(harbour.x, harbour.y - 42, 112, 38);
      this.resolveBox(harbour.x, harbour.y + 102, 34, 54);

      for (const landmark of this.world.landmarks) this.resolveCircle(landmark.x, landmark.y, 76);
      for (const port of this.world.ports) this.resolveCircle(port.x, port.y - 8, 28);
      for (const raft of this.world.rafts) {
        if (!this.rescuedRafts.has(raft.id)) this.resolveCircle(raft.x, raft.y, 32);
      }
      for (const bottle of this.world.bottles) {
        if (!this.readBottles.has(bottle.id)) this.resolveCircle(bottle.x, bottle.y, 13);
      }
      for (const rule of this.world.rules) {
        if (!this.collectedRules.has(rule.id)) this.resolveCircle(rule.x, rule.y, 19);
      }
      for (const fleetBoat of this.world.fleet) this.resolveCircle(fleetBoat.x, fleetBoat.y, 31);
      if (this.world.whale.surfaced) this.resolveCircle(this.world.whale.x, this.world.whale.y, 60);
    }
  }

  private clampBoatToWorld() {
    const b = this.boat;
    const pad = 46;
    if (b.x < pad) {
      b.x = pad;
      if (this.vel.x < 0) this.vel.x = 0;
    }
    if (b.x > this.world.w - pad) {
      b.x = this.world.w - pad;
      if (this.vel.x > 0) this.vel.x = 0;
    }
    if (b.y < pad) {
      b.y = pad;
      if (this.vel.y < 0) this.vel.y = 0;
    }
    if (b.y > this.world.h - pad) {
      b.y = this.world.h - pad;
      if (this.vel.y > 0) this.vel.y = 0;
    }
  }

  /** Advance time + traffic only (title screen / paused): keeps the harbour alive. */
  idle(dt: number) {
    this.time += dt;
    this.advanceAmbient(dt);
  }

  private clampCamera() {
    const over = 160;
    const hw = this.view.w / 2;
    const hh = this.view.h / 2;
    if (this.world.w + over * 2 > this.view.w) {
      this.camera.x = clamp(this.camera.x, hw - over, this.world.w - hw + over);
    } else {
      this.camera.x = this.world.w / 2;
    }
    if (this.world.h + over * 2 > this.view.h) {
      this.camera.y = clamp(this.camera.y, hh - over, this.world.h - hh + over);
    } else {
      this.camera.y = this.world.h / 2;
    }
  }

  private findNearby(): Nearby {
    const b = this.boat;
    let best: Nearby = null;
    let bestScore = Infinity;
    const consider = (n: NonNullable<Nearby>, r: number) => {
      const d = Math.hypot(n.x - b.x, n.y - b.y);
      if (d < r && d < bestScore) {
        best = n;
        bestScore = d;
      }
    };
    for (const p of this.world.ports) {
      consider({ kind: "port", id: p.id, x: p.x, y: p.y, prompt: "dock" }, R.port);
    }
    for (const raft of this.world.rafts) {
      if (this.rescuedRafts.has(raft.id)) continue;
      consider({ kind: "crew", id: raft.id, x: raft.x, y: raft.y, prompt: "bring aboard" }, R.crew);
    }
    for (const bo of this.world.bottles) {
      if (this.readBottles.has(bo.id)) continue;
      consider({ kind: "bottle", id: bo.id, x: bo.x, y: bo.y, prompt: "read" }, R.bottle);
    }
    for (const f of this.world.fleet) {
      consider({ kind: "fleet", id: f.id, x: f.x, y: f.y, prompt: "hail" }, R.fleet);
    }
    for (const lm of this.world.landmarks) {
      consider({ kind: "landmark", id: lm.id, x: lm.x, y: lm.y, prompt: "go ashore" }, R.landmark);
    }
    for (const rule of this.world.rules) {
      if (this.collectedRules.has(rule.id)) continue;
      consider({ kind: "rule", id: rule.id, x: rule.x, y: rule.y, prompt: "collect" }, R.rule);
    }
    if (this.world.whale.surfaced) {
      consider({ kind: "whale", id: "whale", x: this.world.whale.x, y: this.world.whale.y, prompt: "say hello" }, R.whale);
    }
    const lh = this.world.lighthouse;
    consider({ kind: "lighthouse", id: "lighthouse", x: lh.x, y: lh.y, prompt: "read the log" }, R.lighthouse);
    const h = this.world.harbour;
    // Interact at the water-facing end of the landing pier, not through the
    // solid quay wall at the artwork's centre.
    consider({ kind: "harbour", id: "harbour", x: h.x, y: h.y + 165, prompt: "make port" }, R.harbour);
    return best;
  }

  // --- interaction resolvers (called by React on dialog actions) ---
  visitPort(id: string) {
    this.visited.add(id);
  }
  claimPort(id: string) {
    this.visited.add(id);
    this.claimed = id;
  }
  rescueCrew(raftId: string, name: string) {
    const trimmed = name.trim().slice(0, 24);
    if (!trimmed || this.crew.length >= MAX_CREW || this.crew.includes(trimmed)) return;
    this.crew.push(trimmed);
    this.rescuedRafts.add(raftId);
  }
  readBottle(id: string) {
    this.readBottles.add(id);
  }
  readLog() {
    this.logRead = true;
  }
  collectRule(id: string) {
    this.collectedRules.add(id);
  }
  seeLandmark(id: string) {
    this.seenLandmarks.add(id);
  }
  findWhale() {
    this.whaleFound = true;
  }

  portById(id: string) {
    return this.world.ports.find((p) => p.id === id) ?? null;
  }
  bottleById(id: string) {
    return this.world.bottles.find((b) => b.id === id) ?? null;
  }
  fleetById(id: string) {
    return this.world.fleet.find((f) => f.id === id) ?? null;
  }
  landmarkById(id: string) {
    return this.world.landmarks.find((l) => l.id === id) ?? null;
  }
  ruleById(id: string) {
    return this.world.rules.find((r) => r.id === id) ?? null;
  }

  snapshot(): Snapshot {
    return {
      vessel: this.claimed ? (this.portById(this.claimed)?.label ?? null) : null,
      crew: [...this.crew],
      visited: this.visited.size,
      ports: this.world.ports.length,
      notesRead: this.readBottles.size,
      logRead: this.logRead,
      rules: this.collectedRules.size,
      rulesTotal: this.world.rules.length,
      landmarks: this.seenLandmarks.size,
      landmarksTotal: this.world.landmarks.length,
      whaleFound: this.whaleFound,
    };
  }

  serialize(): string {
    return JSON.stringify({
      boat: { x: this.boat.x, y: this.boat.y, heading: this.boat.heading },
      visited: [...this.visited],
      readBottles: [...this.readBottles],
      rescuedRafts: [...this.rescuedRafts],
      collectedRules: [...this.collectedRules],
      seenLandmarks: [...this.seenLandmarks],
      crew: this.crew,
      claimed: this.claimed,
      boatStyle: this.boatStyle,
      logRead: this.logRead,
      whaleFound: this.whaleFound,
    });
  }

  restore(raw: string) {
    try {
      const s = JSON.parse(raw);
      if (s.boat && typeof s.boat.x === "number") {
        this.boat.x = clamp(s.boat.x, 0, this.world.w);
        this.boat.y = clamp(s.boat.y, 0, this.world.h);
        this.boat.heading = typeof s.boat.heading === "number" ? s.boat.heading : this.boat.heading;
        this.camera = { x: this.boat.x, y: this.boat.y };
      }
      if (Array.isArray(s.visited)) this.visited = new Set(s.visited);
      if (Array.isArray(s.readBottles)) this.readBottles = new Set(s.readBottles);
      if (Array.isArray(s.rescuedRafts)) this.rescuedRafts = new Set(s.rescuedRafts);
      if (Array.isArray(s.collectedRules)) this.collectedRules = new Set(s.collectedRules);
      if (Array.isArray(s.seenLandmarks)) this.seenLandmarks = new Set(s.seenLandmarks);
      if (Array.isArray(s.crew)) this.crew = s.crew.filter((c: unknown): c is string => typeof c === "string").slice(0, MAX_CREW);
      if (typeof s.claimed === "string" || s.claimed === null) this.claimed = s.claimed;
      if (isBoatStyleId(s.boatStyle)) this.boatStyle = s.boatStyle;
      this.logRead = Boolean(s.logRead);
      this.whaleFound = Boolean(s.whaleFound);
    } catch {
      /* corrupt save: start fresh */
    }
  }

  reset() {
    this.visited.clear();
    this.readBottles.clear();
    this.rescuedRafts.clear();
    this.collectedRules.clear();
    this.seenLandmarks.clear();
    this.crew = [];
    this.claimed = null;
    this.boatStyle = null;
    this.logRead = false;
    this.whaleFound = false;
    this.boat.x = this.world.dock.x;
    this.boat.y = this.world.dock.y - 100;
    this.boat.heading = -Math.PI / 2;
    this.vel = { x: 0, y: 0 };
    this.camera = { x: this.boat.x, y: this.boat.y };
    this.trail = [];
  }
}
